import type {
  AssetId,
  CustomerId,
  FacilityId,
  WorkRequestId,
} from "../lib/workIds";
import { workRequestSchema } from "../queries/useWorkRequestsQuery";
import type { RequestPriority } from "../types/workRequest";
import {
  duplicateAnalysisSchema,
  workRequestApprovalSchema,
} from "../types/workRequestApi";
import { workApiFetch, workClient } from "./workClient";

export async function getWorkRequestApproval(
  workRequestId: WorkRequestId,
  customerId: CustomerId,
  facilityId: FacilityId,
) {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ].requests[":requestId"].$get({
    param: { customerId, facilityId, requestId: workRequestId },
  });
  if (!response.ok)
    throw new Error(`Unable to load work request (${response.status}).`);
  const request = workRequestSchema.parse(await response.json());
  return workRequestApprovalSchema.parse({
    id: request.id,
    title: request.title,
    description: request.description ?? "",
    severity: request.priority?.code ?? null,
    status: request.status.code,
    submittedAt: request.submittedAt,
    submittedBy: request.requester.name,
    facilityId: null,
    asset: request.asset
      ? {
          id: request.asset.id,
          code: null,
          name: request.asset.name,
          locationName: request.asset.locationName,
        }
      : { id: 0, code: null, name: "Unassigned", locationName: null },
    convertedWorkOrderId: null,
  });
}

export async function analyzeWorkRequestDuplicates(input: {
  workRequestId: WorkRequestId;
  assetId: number;
  description: string;
  customerId: CustomerId;
  facilityId: FacilityId;
}) {
  const response = await workApiFetch(
    `/v1/customers/${encodeURIComponent(input.customerId)}/facilities/${encodeURIComponent(input.facilityId)}/requests/${input.workRequestId}/duplicate-analysis`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        assetId: input.assetId,
        description: input.description,
      }),
    },
  );
  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    const message =
      errorBody && typeof errorBody === "object" && "error" in errorBody
        ? String(errorBody.error)
        : `Unable to analyze duplicates (${response.status}).`;
    throw new Error(message);
  }
  return {
    ...duplicateAnalysisSchema.parse(await response.json()),
    workRequestId: input.workRequestId,
  };
}

export async function approveWorkRequest(input: {
  workRequestId: WorkRequestId;
  title: string;
  description: string;
  priorityCode: RequestPriority;
  confirmDuplicateWarning: boolean;
  assetId?: AssetId;
  workTypeCode: string;
  assignedUserIds: number[];
  assignedTeamIds: number[];
  basicStartDate: string | null;
  basicFinishDate: string | null;
  estimatedMinutes: number | null;
  vendorName: string | null;
  customerId: CustomerId;
  facilityId: FacilityId;
}) {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ].requests[":requestId"].approve.$post({
    param: {
      customerId: input.customerId,
      facilityId: input.facilityId,
      requestId: input.workRequestId,
    },
    json: {
      title: input.title,
      description: input.description,
      priorityCode: input.priorityCode,
      confirmDuplicateWarning: input.confirmDuplicateWarning,
      assetId: input.assetId,
      workTypeCode: input.workTypeCode,
      assignedUserIds: input.assignedUserIds,
      assignedTeamIds: input.assignedTeamIds,
      basicStartDate: input.basicStartDate,
      basicFinishDate: input.basicFinishDate,
      estimatedMinutes: input.estimatedMinutes,
      vendorName: input.vendorName,
    },
  });
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as {
      error?: unknown;
      message?: unknown;
    } | null;
    // Shared ApiError responses carry the readable text in `message` (with
    // `error` as the class name); route-level responses carry it in `error`.
    const specific = [errorBody?.message, errorBody?.error].find(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    );
    throw new Error(
      specific ?? `Unable to approve work request (${response.status}).`,
    );
  }
  return response.json();
}
